import * as React from "react";
import * as styles from "./ChromaFlare.css";
import { ChromaFlareScene } from "./ChromaFlareScene";

type FullscreenElement = HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenDocument = Document & {
    webkitExitFullscreen?: () => Promise<void> | void;
    webkitFullscreenElement?: Element | null;
};

const SceneWidth = 500;
const SceneHeight = 270;
const FullscreenScaleProperty = "--chroma-fullscreen-scale";

export function ChromaFlare() {
    const [flareCycle, setFlareCycle] = React.useState(0);
    const [isFullscreen, setIsFullscreen] = React.useState(false);
    const demoRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const updateFullscreenState = () => {
            const fullscreen = IsFullscreen();
            const demo = demoRef.current;

            setIsFullscreen(fullscreen);

            if (!demo) {
                return;
            }

            if (!fullscreen) {
                demo.style.removeProperty(FullscreenScaleProperty);
                return;
            }

            const scale = Math.min(window.innerWidth / SceneWidth, window.innerHeight / SceneHeight);
            demo.style.setProperty(FullscreenScaleProperty, String(scale));
        };

        document.addEventListener("fullscreenchange", updateFullscreenState);
        document.addEventListener("webkitfullscreenchange", updateFullscreenState);
        window.addEventListener("resize", updateFullscreenState);
        updateFullscreenState();

        return () => {
            document.removeEventListener("fullscreenchange", updateFullscreenState);
            document.removeEventListener("webkitfullscreenchange", updateFullscreenState);
            window.removeEventListener("resize", updateFullscreenState);
        };
    }, []);

    const toggleFullscreen = async () => {
        const fullscreenDocument = document as FullscreenDocument;

        try {
            if (IsFullscreen()) {
                if (document.exitFullscreen) {
                    await document.exitFullscreen();
                } else if (fullscreenDocument.webkitExitFullscreen) {
                    await fullscreenDocument.webkitExitFullscreen();
                }
                return;
            }

            const element = demoRef.current as FullscreenElement | null;
            if (!element) {
                return;
            }

            if (element.requestFullscreen) {
                await element.requestFullscreen();
            } else if (element.webkitRequestFullscreen) {
                await element.webkitRequestFullscreen();
            }
        } catch {
            setIsFullscreen(false);
        }
    };

    return (
        <div ref={demoRef} className={styles.demo}>
            <div
                key={flareCycle}
                style={{ display: "contents" }}
                role="img"
                aria-label="A clear glass environment with a moving point of light, diamond-shaped refraction and lens flare"
            >
                <ChromaFlareScene />
            </div>
            <div className={styles.actions}>
                <button
                    className={styles.replayButton}
                    type="button"
                    onClick={() => setFlareCycle((cycle) => cycle + 1)}
                >
                    Replay flare
                </button>
                <button
                    className={styles.fullscreenButton}
                    type="button"
                    aria-pressed={isFullscreen}
                    onClick={() => void toggleFullscreen()}
                >
                    {isFullscreen ? "Exit full screen" : "Full screen"}
                </button>
            </div>
        </div>
    );
}

function IsFullscreen() {
    const fullscreenDocument = document as FullscreenDocument;
    return Boolean(document.fullscreenElement || fullscreenDocument.webkitFullscreenElement);
}
